<template lang="pug">
.banner-block(
  v-if="bannerBlock.items.length > 0"
  :class="{ 'full-width': bannerBlock.setFullWidth }"
)
  // Single banner - static image
  .single-banner(v-if="bannerBlock.items.length === 1")
    .banner-item(@click="handleBannerClick(bannerBlock.items[0])")
      img(
        :src="bannerBlock.items[0].imageUrl"
        :alt="bannerBlock.title || 'Banner'"
        @error="handleImageError"
      )

  // Multiple banners - carousel
  .carousel-container(v-else)
    Carousel(
      :autoplay="5000"
      :wrap-around="true"
      :transition="500"
      :items-to-show="1"
      :snap-align="'center'"
      :breakpoints="carouselBreakpoints"
    )
      Slide(
        v-for="(item, index) in bannerBlock.items"
        :key="index"
      )
        .banner-item(@click="handleBannerClick(item)")
          img(
            :src="item.imageUrl"
            :alt="bannerBlock.title || `Banner ${index + 1}`"
            @error="handleImageError"
          )
      template(#addons)
        Navigation
        Pagination
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { useRouter } from 'vue-router';
import {
  Carousel, Slide, Pagination, Navigation,
} from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { BannerBlock as BannerBlockType, BannerItem } from '../types';

export default defineComponent({
  name: 'BannerBlock',
  components: {
    Carousel,
    Slide,
    Pagination,
    Navigation,
  },
  props: {
    block: {
      type: Object as PropType<BannerBlockType>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();

    // Computed
    const bannerBlock = computed(() => props.block as BannerBlockType);

    const carouselBreakpoints = computed(() => ({
      // Mobile
      0: {
        itemsToShow: 1,
        snapAlign: 'center',
      },
      // Tablet and up
      768: {
        itemsToShow: bannerBlock.value.setFullWidth ? 1 : 1,
        snapAlign: bannerBlock.value.setFullWidth ? 'start' : 'center',
      },
    }));

    // Methods
    const isExternalUrl = (url: string): boolean => {
      try {
        const uri = new URL(url);
        return uri.protocol === 'http:' || uri.protocol === 'https:';
      } catch {
        return false;
      }
    };

    const mapDeepLinkToRoute = (deepLink: string): string => {
      // Map deep link patterns to actual app routes
      const mappings = [
        {
          // Map /sticker-details/:id to /web/sticker/:id
          pattern: /^\/sticker-details\/(.+)$/,
          replacement: '/web/sticker/$1',
        },
        {
          // Map /profile/organization/:id to /web/profile/organization/:id
          pattern: /^\/profile\/organization\/(.+)$/,
          replacement: '/web/profile/organization/$1',
        },
      ];

      for (const mapping of mappings) {
        if (mapping.pattern.test(deepLink)) {
          return deepLink.replace(mapping.pattern, mapping.replacement);
        }
      }

      // If no mapping found, return original link
      return deepLink;
    };

    const handleBannerClick = async (item: BannerItem) => {
      const { deepLink } = item;

      if (!deepLink || deepLink.trim() === '') return;

      if (isExternalUrl(deepLink)) {
        // Open external URL in new tab
        window.open(deepLink, '_blank', 'noopener,noreferrer');
      } else {
        // Map deep link to actual route and navigate
        const mappedRoute = mapDeepLinkToRoute(deepLink);
        try {
          await router.push(mappedRoute);
        } catch (error) {
          console.error('Navigation error:', error);
        }
      }
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      // Set a placeholder or hide the image
      target.style.display = 'none';
      console.error('Failed to load banner image:', target.src);
    };

    return {
      bannerBlock,
      carouselBreakpoints,
      handleBannerClick,
      handleImageError,
    };
  },
});
</script>

<style lang="scss" scoped>
.banner-block {
  margin-bottom: 48px;
  padding: 0 24px;
  position: relative;
  z-index: 1;

  // Add top margin on mobile only to prevent overlap with header
  margin-top: 50px;

  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 56px;
    margin-top: 0;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 64px;
  }

  &.full-width {
    padding: 0;
    margin-bottom: 0;

    .banner-item {
      border-radius: 0;
    }

    // On desktop, ignore full-width and show normal banner style
    @media (min-width: 768px) {
      padding: 0 40px;
      margin-bottom: 56px;

      .banner-item {
        border-radius: 16px;
      }
    }

    @media (min-width: 1024px) {
      padding: 0 64px;
      margin-bottom: 64px;
    }
  }
}

.single-banner,
.carousel-container {
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  .banner-item {
    width: 100%;
  }
}

.banner-item {
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 1;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(13, 148, 136, 0.15);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover img {
    transform: scale(1.05);
  }
}

@media (min-width: 768px) {
  .banner-block:not(.full-width) .banner-item {
    max-height: 28rem;
  }
}

@media (min-width: 1200px) {
  .banner-block:not(.full-width) .banner-item {
    max-height: 32rem;
  }
}

// Vue3 Carousel customization
:deep(.carousel__viewport) {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.carousel__track) {
  margin: 0 !important;
}

:deep(.carousel__slide) {
  padding: 0 8px;

  @media (min-width: 768px) {
    padding: 0 10px;
  }

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  .banner-item {
    width: 100%;
    height: auto;
  }
}

:deep(.carousel__pagination) {
  margin-top: 16px;
  padding: 0;
}

:deep(.carousel__pagination-button) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #d1d5db;
  border: none;
  padding: 0;
  margin: 0 6px;
  transition: all 0.3s;

  &::after {
    display: none;
  }

  &:hover {
    background-color: #9ca3af;
  }

  &.carousel__pagination-button--active {
    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
    width: 24px;
    border-radius: 4px;
  }
}

:deep(.carousel__prev),
:deep(.carousel__next) {
  display: none !important;
}

.full-width {
  :deep(.carousel__viewport) {
    border-radius: 0;

    // On desktop, restore normal border-radius
    @media (min-width: 768px) {
      border-radius: 16px;
    }
  }

  :deep(.carousel__slide) {
    padding: 0;

    // On desktop, restore normal padding
    @media (min-width: 768px) {
      padding: 0 10px;

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }
    }
  }

  .banner-item {
    border-radius: 0;

    // On desktop, restore normal border-radius
    @media (min-width: 768px) {
      border-radius: 16px;
    }
  }
}
</style>
